import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TranslateModule } from '@ngx-translate/core';
import { CommonExplainModule } from '../../common-explain/common-explain.module';
import { ApiService } from '../../shared/services/api/api.service';
import { Prompt } from '../../models/prompt';
import { MatIcon } from '@angular/material/icon';
import { TreeviewItem } from '../../lib/ngx-treeview';
import { NgbTooltip } from '@ng-bootstrap/ng-bootstrap';
import {
  SmartSearchSource,
  SMART_SEARCH_SESSIONS_KEY,
  TAB_ID_KEY,
} from '../../shared/services/smart-search.service';
import { Subscription } from 'rxjs';
import { TerritoryHelper } from '../../shared/helpers/territory.helper';

export interface SmartSearchConfig {
  topic?: string;
  topicId?: number;
  perimeter?: string[];
  territories?: string[];
  territoryTree?: TreeviewItem[];
  adminDocSources?: string[];
  pressSources?: string[];
  keywords?: string;
  impacterId?: string | null;
}

@Component({
  selector: 'app-smart-search-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    TranslateModule,
    CommonExplainModule,
    MatIcon,
    NgbTooltip,
  ],
  templateUrl: './smart-search-modal.component.html',
  styleUrls: ['./smart-search-modal.component.scss'],
})
export class SmartSearchModalComponent implements OnInit, OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Input() searchConfig: SmartSearchConfig = {};
  @Output() closeModal = new EventEmitter<void>();
  @ViewChild('copyTooltip', { static: false }) copyTooltip!: NgbTooltip;

  constructor(private apiService: ApiService) {}

  selectedSources = {
    [SmartSearchSource.PRESS]: true,
    [SmartSearchSource.ADMIN_DOC]: true,
    [SmartSearchSource.WEB]: false,
  };
  prompts: Prompt[] = [];
  filledPrompt = { title: '', text: '' };
  displayedPerimeter = '';
  modalPerimeter = '';
  inWritingPromptMode = false;
  currentSessionId: string | null = null;

  promptsSubscription!: Subscription;

  private _displayedPromptText = '';

  // Getter/setter for two-way binding with territory replacement
  get displayedPromptText(): string {
    return this._displayedPromptText;
  }

  set displayedPromptText(value: string) {
    this._displayedPromptText = value;
    // Synchronize with filledPrompt.text when user types, replacing territory text with [territoire] tag
    if (this.displayedPerimeter && this.displayedPerimeter.trim() !== '' && value.includes(this.displayedPerimeter)) {
      // Replace the displayedPerimeter text with [territoire] tag
      this.filledPrompt.text = value.split(this.displayedPerimeter).join('[territoire]');
    } else {
      this.filledPrompt.text = value; // No territory text to preserve
    }
  }


  ngOnInit() {
    this.promptsSubscription = this.apiService.prompts
      .retrievePrompts()
      .subscribe((prompts) => {
        this.prompts = prompts;
      });
    if (this.searchConfig.impacterId) {
      this.changeSourceStatus(SmartSearchSource.ADMIN_DOC);
    }
    this.updateDisplayedPerimeter();
    this.initializeTabSessionId();
    this.loadConfigFromStorage();
    this.updateSmartSearchSession();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes['isOpen']?.currentValue === true &&
      changes['isOpen']?.previousValue === false &&
      this.currentSessionId
    ) {
      this.updateSmartSearchSession();
    }
  }

  initializeTabSessionId(): void {
    let tabId = sessionStorage.getItem(TAB_ID_KEY);
    if (!tabId) {
      tabId = `tab_${Date.now()}`;
      sessionStorage.setItem(TAB_ID_KEY, tabId);
    }
    this.currentSessionId = tabId;
  }

  loadConfigFromStorage(): void {
    if (!this.currentSessionId) {
      this.initializeTabSessionId();
    }
    if (!this.currentSessionId) {
      return;
    }
    const sessionsJson = sessionStorage.getItem(SMART_SEARCH_SESSIONS_KEY);
    if (!sessionsJson) {
      return;
    }
    const sessions = JSON.parse(sessionsJson);
    const savedData = sessions[this.currentSessionId];
    if (savedData) {
      this.restoreSavedData(savedData);
    }
  }

  private restoreSavedData(savedData: any): void {
    this.restorePrompt(savedData);
    this.restoreSources(savedData);
  }

  private restorePrompt(savedData: any): void {
    if (savedData.prompt) {
      this.filledPrompt.text = savedData.prompt;
      this.updateDisplayedPromptText();
    }
    if (savedData.promptTitle) {
      this.filledPrompt.title = savedData.promptTitle;
    }
    if (savedData.prompt?.trim()) {
      this.inWritingPromptMode = true;
    }
  }

  private restoreSources(savedData: any): void {
    if (savedData.sources && Array.isArray(savedData.sources)) {
      this.selectedSources = {
        [SmartSearchSource.PRESS]: savedData.sources.includes(
          SmartSearchSource.PRESS
        ),
        [SmartSearchSource.ADMIN_DOC]: savedData.sources.includes(
          SmartSearchSource.ADMIN_DOC
        ),
        [SmartSearchSource.WEB]: savedData.sources.includes(
          SmartSearchSource.WEB
        ),
      };
    }
  }

  changeSourceStatus(source: SmartSearchSource): void {
    this.selectedSources[source] = !this.selectedSources[source];
    this.updateSmartSearchSession();
  }

  updateDisplayedPerimeter() {
    if (
      !this.searchConfig.territories ||
      this.searchConfig.territories.length === 0
    ) {
      this.displayedPerimeter = '';
      this.modalPerimeter = '';
      return;
    }

    const { territoryTree = [] } = this.searchConfig;
    const [displayedPromptPerimeter, displayedModalPerimeter] = TerritoryHelper.getDisplayedPerimeter(territoryTree);
    this.displayedPerimeter = displayedPromptPerimeter;
    this.modalPerimeter = displayedModalPerimeter;
  }

  onClose(): void {
    this.updateSmartSearchSession();
    this.closeModal.emit();
  }

  selectPrompt(prompt: Prompt): void {
    this.filledPrompt = prompt;
    this.updateDisplayedPromptText();
    this.inWritingPromptMode = true;
    this.updateSmartSearchSession();
  }

  clearPrompt(): void {
    this.filledPrompt = { title: '', text: '' };
    this._displayedPromptText = '';
    this.inWritingPromptMode = false;
    this.updateSmartSearchSession();
  }

  private replaceTerritoryPlaceholder(text: string): string {
    let result = text;

    return result.replaceAll(
      /\[territoire\]/gi,
      this.displayedPerimeter || 'le territoire'
    );
  }

  onTextareaFocus(): void {
    this.inWritingPromptMode = true;
  }

  onPromptTextChange(): void {
    this.updateSmartSearchSession();
  }

  updateDisplayedPromptText(): void {
    this._displayedPromptText = this.replaceTerritoryPlaceholder(
      this.filledPrompt.text
    );
  }

  copyPrompt(): void {
    if (this._displayedPromptText) {
      navigator.clipboard
        .writeText(this._displayedPromptText)
        .then(() => {
          if (this.copyTooltip) {
            this.copyTooltip.open();
            setTimeout(() => {
              this.copyTooltip.close();
            }, 2000);
          }
        })
        .catch((err) => {
          console.error('Failed to copy prompt:', err);
        });
    }
  }

  canTriggerSmartSearch(): boolean {
    const hasPrompt = this.filledPrompt.text.trim().length > 0;
    const selectedSourcesList = Object.entries(this.selectedSources)
      .filter(([_, isSelected]) => isSelected)
      .map(([source]) => source);
    if (!hasPrompt) return false;
    if (selectedSourcesList.length === 0) return false;
    return !(
      selectedSourcesList.length === 1 &&
      selectedSourcesList[0] === SmartSearchSource.WEB
    );
  }

  getTooltipMessage(): string {
    const hasPrompt = this.filledPrompt.text.trim().length > 0;
    const selectedSourcesList = Object.entries(this.selectedSources)
      .filter(([_, isSelected]) => isSelected)
      .map(([source]) => source);

    if (!hasPrompt) {
      return 'search.smart-search-modal.specify-analysis';
    }
    if (selectedSourcesList.length === 0) {
      return 'search.smart-search-modal.select-at-least-one-source';
    }
    if (
      selectedSourcesList.length === 1 &&
      selectedSourcesList[0] === SmartSearchSource.WEB
    ) {
      return 'search.smart-search-modal.select-another-source';
    }
    return '';
  }

  shouldShowTooltip(): boolean {
    return !this.canTriggerSmartSearch();
  }

  TriggerSmartSearch(): void {
    if (!this.currentSessionId) {
      return;
    }
    const searchSessionId = `search_${Date.now()}_${Math.random()
      .toString(36)
      .substring(2, 9)}`;
    this.updateSmartSearchSession();
    const sessionsStr = sessionStorage.getItem(SMART_SEARCH_SESSIONS_KEY) || '{}';
    const sessions = JSON.parse(sessionsStr);
    const currentConfig = sessions[this.currentSessionId];
    if (currentConfig) {
      sessions[searchSessionId] = currentConfig;
      sessionStorage.setItem(SMART_SEARCH_SESSIONS_KEY, JSON.stringify(sessions));
    }
    window.open(`/smart-search?sessionId=${searchSessionId}`, '_blank');
  }

  updateSmartSearchSession(): void {
    if (!this.currentSessionId) {
      return;
    }

    const smartSearchConfig = {
      prompt: this.filledPrompt.text,
      promptMap: {
        territoire: this.displayedPerimeter,
      },
      promptTitle: this.filledPrompt.title,
      sources: Object.entries({...this.selectedSources, ...(this.searchConfig.impacterId ? {[SmartSearchSource.ADMIN_DOC]: false} : {})})
        .filter(([_, isSelected]) => isSelected)
        .map(([source, _]) => source),
      territories: this.searchConfig.territories || [],
      perimeter: this.searchConfig.perimeter || [],
      topicId: this.searchConfig.topicId || null,
      keywords: this.searchConfig.keywords || '',
      pressSources: this.searchConfig.pressSources || [],
      adminDocSources: this.searchConfig.adminDocSources || [],
      impacterId: this.searchConfig.impacterId || null,
    };

    const sessionsStr = sessionStorage.getItem(SMART_SEARCH_SESSIONS_KEY) || '{}';
    const sessionsJson: Record<string, any> = JSON.parse(sessionsStr);
    sessionsJson[this.currentSessionId] = smartSearchConfig;
    sessionStorage.setItem(
      SMART_SEARCH_SESSIONS_KEY,
      JSON.stringify(sessionsJson)
    );
  }

  ngOnDestroy(): void {
    if (this.promptsSubscription) {
      this.promptsSubscription.unsubscribe();
    }
  }

  protected readonly SmartSearchSource = SmartSearchSource;
}
